import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { toast } from 'react-hot-toast';
import { api } from '../../services/authService';
import { SIGNAL_PROVIDER } from '../../utils/signalProviders';

/** Deep link to a Fast-ID report (shared by the report page's share button and the list's share icon). */
export function fastIdReportUrl(executionId) {
  return `${window.location.origin}/fastid-report/${executionId}`;
}

export async function copyFastIdReportLink(executionId) {
  try {
    await navigator.clipboard.writeText(fastIdReportUrl(executionId));
    toast.success('Link copied to clipboard');
  } catch (err) {
    console.error('Copy report link error:', err);
    toast.error('Failed to copy link');
  }
}

/** GET .../fast_id_purchase_download?execution_id=... (302 -> presigned S3 URL). */
export function fastIdPurchaseDownloadHref(executionId) {
  return `/api/v2/fast_id_purchase_download?${new URLSearchParams({ execution_id: executionId })}`;
}

export function downloadFastIdPurchase(executionId) {
  window.open(fastIdPurchaseDownloadHref(executionId), '_blank', 'noopener,noreferrer');
}

/** Sentinel and options aligned with Dashboard.js domain filter. */
export const FAST_ID_DOMAIN_SLICE_ALL = '__all__';

export const FAST_ID_DOMAIN_SLICE_OPTIONS = [
  { value: FAST_ID_DOMAIN_SLICE_ALL, label: 'All' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'apple', label: 'Apple' },
  { value: 'yahoo', label: 'Yahoo' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'aol', label: 'AOL' },
  { value: 'other', label: 'Other' },
];

export async function fetchFastIdReports(executionId, domainSlice = FAST_ID_DOMAIN_SLICE_ALL) {
  const params = { execution_id: executionId };
  if (domainSlice && domainSlice !== FAST_ID_DOMAIN_SLICE_ALL) {
    params.domain = domainSlice;
  }
  const [repRes, mrRes] = await Promise.all([
    api.get('/api/v2/fast_id_report', { params }),
    api.get('/api/v2/fast_id_match_rate', { params }),
  ]);
  return { report: repRes.data, matchRate: mrRes.data };
}

/** Match Dashboard.js doughnut styling */
const CHART_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

function toChartData(fields, preserveOrder = false) {
  const normalized = fields
    .map((item) => ({ ...item, value: Number(item.value) || 0 }))
    .filter(({ value }) => value > 0);
  const sorted = preserveOrder ? normalized : [...normalized].sort((a, b) => b.value - a.value);
  return sorted.map((item, i) => ({ ...item, color: CHART_COLORS[i % CHART_COLORS.length] }));
}

const FAST_ID_CHART_HEIGHT = 200;

/** Icon + title row (dashboard-style circular badge). */
function FastIdSectionTitle({ iconSrc, iconBg, children }) {
  return (
    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
      <span
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: iconBg ?? '#f3f4f6' }}
      >
        <img src={iconSrc} alt="" className="w-4 h-4" />
      </span>
      {children}
    </h3>
  );
}

function FastIdChartCard({ title, data, iconSrc, iconBg }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 min-w-0 overflow-visible">
      <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
        {iconSrc && (
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: iconBg }}
          >
            <img src={iconSrc} alt="" className="w-4 h-4" />
          </span>
        )}
        {title}
      </h3>
      {!data || data.length === 0 ? (
        <div
          className="flex items-center justify-center text-gray-500 text-xs"
          style={{ height: FAST_ID_CHART_HEIGHT }}
        >
          No data currently available
        </div>
      ) : (
        (() => {
          const total = data.reduce((acc, d) => acc + d.value, 0);
          return (
            <div className="flex items-center gap-2 w-full" style={{ height: FAST_ID_CHART_HEIGHT }}>
              <div className="flex-1 min-w-0" style={{ height: FAST_ID_CHART_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                    <Pie
                      data={data}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={36}
                      outerRadius={72}
                      paddingAngle={0}
                      minAngle={1}
                      labelLine={false}
                      label={({ percent, x, y }) => {
                        if (percent < 0.02) return null;
                        const pct = `${(percent * 100).toFixed(1)}%`;
                        const padding = 4;
                        const fontSize = 9;
                        const width = Math.max(pct.length * 5 + padding * 2, 26);
                        const height = fontSize + padding * 2;
                        return (
                          <g>
                            <rect
                              x={x - width / 2}
                              y={y - height / 2}
                              width={width}
                              height={height}
                              fill="white"
                              stroke="#e5e7eb"
                              strokeWidth={1}
                              rx={2}
                            />
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize={fontSize}
                              fill="#374151"
                            >
                              {pct}
                            </text>
                          </g>
                        );
                      }}
                    >
                      {data.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        total ? `${((100 * value) / total).toFixed(1)}%` : value,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col gap-y-1 flex-shrink-0 text-sm leading-snug">
                {data.map((entry, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span>{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}

/** Display order for match-rate rows (sort_order values from pipeline). */
const FAST_ID_MATCH_RATE_SORT_ORDER = [5, 1, 4, 2, 3, 7, 6];

function sortFastIdMatchRateRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const rank = new Map(FAST_ID_MATCH_RATE_SORT_ORDER.map((n, i) => [n, i]));
  return [...rows].sort((a, b) => {
    const soA = a?.sort_order;
    const soB = b?.sort_order;
    const ia = rank.has(soA) ? rank.get(soA) : FAST_ID_MATCH_RATE_SORT_ORDER.length;
    const ib = rank.has(soB) ? rank.get(soB) : FAST_ID_MATCH_RATE_SORT_ORDER.length;
    if (ia !== ib) return ia - ib;
    return String(a?.status ?? '').localeCompare(String(b?.status ?? ''));
  });
}

function formatMatchRatePercent(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n >= 0 && n <= 1) return `${(n * 100).toFixed(1)}%`;
  return `${n.toFixed(1)}%`;
}

function formatMatchRateCount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

/** API match-rate status “Submitted” is the same funnel step as `distinct_record_received_ct` — same label as Summary. */
function formatFastIdMatchRateStatus(status) {
  if (status == null || status === '') return '—';
  if (String(status) === 'Submitted') return 'Unique Rows Submitted';
  return String(status);
}

function formatAvgFastIdScore(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

/** Calendar date from fast_id_report.record_insert_datetime_est (API ISO string or Date). */
function formatSubmissionDate(value) {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/** Date submitted + signal_provider from fast_id_report row */
function FastIdSubmissionDetails({ report }) {
  const r = report && typeof report === 'object' ? report : null;
  const dateSubmitted =
    r?.record_insert_datetime_est != null ? formatSubmissionDate(r.record_insert_datetime_est) : '—';
  const fileNameCol =
    r?.signal_provider != null && String(r.signal_provider).trim() !== ''
      ? String(r.signal_provider)
      : '—';

  return (
    <div>
      <FastIdSectionTitle iconSrc="/send.svg" iconBg="#E8EEF5">
        Submission Details
      </FastIdSectionTitle>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700 w-1/3">Date Submitted</th>
              <th className="px-3 py-2 font-medium text-gray-700">File Name</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 text-gray-900 align-top whitespace-nowrap">
                {dateSubmitted}
              </td>
              <td className="px-3 py-2 text-gray-900 break-all">{fileNameCol}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** distinct_record_received_ct, rows_to_purchase, avg_activity_score from fast_id_report */
function FastIdSummaryResults({ report }) {
  const r = report && typeof report === 'object' ? report : null;

  return (
    <div>
      <FastIdSectionTitle iconSrc="/bar-chart.svg" iconBg="#E8F5F3">
        Summary Results
      </FastIdSectionTitle>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">Unique Rows Submitted</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">Rows To Purchase</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">Avg FastID Score</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 text-right text-gray-900 tabular-nums">
                {formatMatchRateCount(r?.distinct_record_received_ct)}
              </td>
              <td className="px-3 py-2 text-right text-gray-900 tabular-nums">
                {formatMatchRateCount(r?.rows_to_purchase)}
              </td>
              <td className="px-3 py-2 text-right text-gray-900 tabular-nums">
                {formatAvgFastIdScore(r?.avg_activity_score)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Table from /api/v2/fast_id_match_rate, rows ordered by sort_order: 5,1,4,2,3,7,6 */
function FastIdMatchRateTable({ matchRate }) {
  const rows = sortFastIdMatchRateRows(matchRate);
  return (
    <div className="mb-4">
      <FastIdSectionTitle iconSrc="/percent.svg" iconBg="#FFF7ED">
        FastID &amp; Signal Match Rate
      </FastIdSectionTitle>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700">FastID Analysis</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">FastID Hashes</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">% Submitted</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">Signals</th>
              <th className="px-3 py-2 font-medium text-gray-700 text-right">% Signal</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                  No match rate data yet.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={`${row.status}-${idx}`}
                  className="border-b border-gray-100 last:border-0"
                >
                  <td className="px-3 py-2 text-gray-900">
                    {formatFastIdMatchRateStatus(row.status)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800 tabular-nums">
                    {formatMatchRateCount(row.fast_id_results)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800 tabular-nums">
                    {formatMatchRatePercent(row.fast_id_results_percent)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800 tabular-nums">
                    {formatMatchRateCount(row.signals_results)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800 tabular-nums">
                    {formatMatchRatePercent(row.signals_results_percent)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Same four chart families as Dashboard signal-analysis doughnuts, fed from fast_id_report row */
function FastIdReportCharts({ report }) {
  const r = report && typeof report === 'object' ? report : {};

  const signalProvidersData = toChartData([
    { name: SIGNAL_PROVIDER.liveIntent.label, value: r.live_intent_md5_ct ?? 0 },
    { name: SIGNAL_PROVIDER.ipost.label, value: r.ipost_md5_ct ?? 0 },
    { name: SIGNAL_PROVIDER.traverse.label, value: r.traverse_md5_ct ?? 0 },
    { name: 'Other', value: r.other_md5_ct ?? 0 },
  ]);

  const domainSummaryData = toChartData([
    { name: 'Yahoo', value: r.yahoo_md5_ct ?? 0 },
    { name: 'Gmail', value: r.gmail_md5_ct ?? 0 },
    { name: 'AOL', value: r.aol_md5_ct ?? 0 },
    { name: 'Microsoft', value: r.microsoft_md5_ct ?? 0 },
    { name: 'Apple', value: r.apple_md5_ct ?? 0 },
    { name: 'Other', value: r.other_domain_md5_ct ?? 0 },
  ]);

  const activeHashesData = toChartData(
    [
      { name: '0–30 days', value: r.md5_last_signal_0_30d_ct ?? 0 },
      { name: '31–60 days', value: r.md5_last_signal_31_60d_ct ?? 0 },
      { name: '61–90 days', value: r.md5_last_signal_61_90d_ct ?? 0 },
      { name: '91+ days', value: r.md5_last_signal_91_plus_ct ?? 0 },
    ],
    true
  );

  const activityScoreData = toChartData(
    [
      { name: '9–10', value: r.activity_score_9_10 ?? 0 },
      { name: '7–8', value: r.activity_score_7_8 ?? 0 },
      { name: '5–6', value: r.activity_score_5_6 ?? 0 },
      { name: '3–4', value: r.activity_score_3_4 ?? 0 },
      { name: '1–2', value: r.activity_score_1_2 ?? 0 },
      { name: 'No Signal', value: r.md5_emails_with_no_signal_ct ?? 0 },
    ],
    true
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      <FastIdChartCard
        title="Signal Providers"
        data={signalProvidersData}
        iconSrc="/radio.svg"
        iconBg="#E5EFFE"
      />
      <FastIdChartCard
        title="Domain Breakdown"
        data={domainSummaryData}
        iconSrc="/globe.svg"
        iconBg="#E8F6F5"
      />
      <FastIdChartCard
        title="Active Hashes"
        data={activeHashesData}
        iconSrc="/check-circle.svg"
        iconBg="#FEF5ED"
      />
      <FastIdChartCard
        title="Activity Score"
        data={activityScoreData}
        iconSrc="/star.svg"
        iconBg="#FEEDF5"
      />
    </div>
  );
}

/** Submission details + summary + match rate table + charts, in the layout shared by the Fast-ID report page. */
export function FastIdReportSections({ report, matchRate }) {
  return (
    <>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <FastIdSubmissionDetails report={report} />
        <FastIdSummaryResults report={report} />
      </div>
      <FastIdMatchRateTable matchRate={matchRate} />
      <FastIdReportCharts report={report} />
    </>
  );
}
